import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import { action } from "typesafe-actions";
import { IPolicyNote } from "./notes.types";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { api } from "../../api/api";
import { getUniqueId } from "../../helpers";
import { makeApiCall } from "../../helpers/postRequest";
import { uploadMultipleFile } from "../../features/FileUpload/utils";
import { IFileUpload } from "../../features/FileUpload/components/FileUpload.type";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";

export const FETCH_NOTE_LIST_PROGRESS = "FETCH_NOTE_LIST_PROGRESS";
export const FETCH_NOTE_LIST_SUCCESS = "FETCH_NOTE_LIST_SUCCESS";
export const FETCH_NOTE_LIST_FAILED = "FETCH_NOTE_LIST_FAILED";

export const fetchNoteListProgress = () => action(FETCH_NOTE_LIST_PROGRESS);
export const fetchNoteListSuccess = (data: IPolicyNote[]) =>
  action(FETCH_NOTE_LIST_SUCCESS, { data });
export const fetchNoteListFailed = () => action(FETCH_NOTE_LIST_FAILED);

export const fetchNoteListFromPolicyAsync =
  (
    customerPolicyId?: number,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchNoteListProgress());
      let finalUrl = `/general/get-notes`;
      if (customerPolicyId) {
        finalUrl = finalUrl + `?customer_policy_id=${customerPolicyId}`;
      }
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }
      const res = await api.get(finalUrl);
      const data: IPolicyNote[] = res.data.data;
      dispatch(fetchNoteListSuccess(data));
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const fetchNoteListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue: IDatatableV2AdvancedSearchFilter
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchNoteListProgress());

      let finalUrl = `/general/get-notes?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}`;
      if (searchValue.length > 0) {
        finalUrl = `/general/get-notes?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${
          date.fromDate
        }&to_date=${date.toDate}&advanceFilter=${JSON.stringify(searchValue)}`;
      }
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }
      const res = await api.get(finalUrl);
      const data: IPolicyNote[] = res.data.data;
      dispatch(fetchNoteListSuccess(data));
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_NOTE_PROGRESS = "FETCH_NOTE_PROGRESS";
export const FETCH_NOTE_SUCCESS = "FETCH_NOTE_SUCCESS";
export const FETCH_NOTE_FAILED = "FETCH_NOTE_FAILED";

export const fetchNoteProgress = () => action(FETCH_NOTE_PROGRESS);
export const fetchNoteSuccess = (data: IPolicyNote) =>
  action(FETCH_NOTE_SUCCESS, { data: data });
export const fetchNoteFailed = (errorMessage: string) =>
  action(FETCH_NOTE_FAILED, { errorMessage });

export const upsertNoteFromPolicyAysnc =
  (
    note: IPolicyNote,
    customerId: number,
    policy_type: string,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        customer_id: customerId,
        customer_policy_id: note.customer_policy_id,
        policy_type,
        page_name: "Notes",
      };
      const { paths } = await uploadMultipleFile(
        note.file_upload || [],
        "POLICY",
        asPayload
      );
      await api.post("/general/upsert-notes", { ...note, file_upload: paths });
      dispatch(
        showMessage({
          type: "success",
          message: "Note saved succesfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertNoteAysnc =
  (
    note: IPolicyNote,
    initialData: IPolicyNote,
    isUpdate: boolean,
    onCallback: (isSuccess: boolean) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        customer_id: note.customer,
        customer_policy_id: note.customer_policy_id,
        page_name: "Notes",
      };
      const { paths } = await uploadMultipleFile(
        note.file_upload || [],
        "POLICY",
        asPayload
      );
      await makeApiCall(
        {
          url: "/general/upsert-notes",
          method: "POST",
          automation: {
            isUpdate: isUpdate,
            primaryFieldName: "notes_id",
          },
        },
        initialData,
        { ...note, file_upload: paths }
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Note saved succesfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchNoteAsync =
  (
    customerPolicyId: number,
    serialNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchNoteProgress());

      const res = await api.get(
        `/general/get-notes?customer_policy_id=${customerPolicyId}&incremental_number=${serialNumber}`
      );
      const data = res.data.data;
      if (data.length > 0) {
        const attachments: IFileUpload[] = [];
        for (const item of (data[0].file_upload || []) as any) {
          attachments.push({
            file: null,
            key: getUniqueId(),
            path: item,
          });
        }
        dispatch(fetchNoteSuccess({ ...data[0], file_upload: attachments }));
      } else {
        dispatch(
          fetchNoteFailed(
            "Unfortunately, there are no records available at the moment."
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchNoteFailed("Something went to be wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const CLEAR_NOTE_STATE = "CLEAR_NOTE_STATE";
export const CLEAR_NOTE = "CLEAR_NOTE";

export const clearNotesState = () => action(CLEAR_NOTE_STATE);
export const clearNote = () => action(CLEAR_NOTE);
